import {Component, DestroyRef, inject} from '@angular/core';
import {FormMode} from '../../../shared/types/common';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ManagementService} from '../management.service';
import {StateService} from '../../../services/state.service';
import {CustomValidators} from '../../../shared/validators';
import {objectArrayToSelectOptions} from '../../../utils/mappings';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AccountPayload, AccountUsersResponse, BrandSummary} from '../types';
import {TitledInputComponent} from '../../../shared/components/titled-input/titled-input.component';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {UtilService} from '../../../services/util.service';
import {ResultData} from '../../../shared/components/search-result-list/search-result-list.component.types';
import {SelectItem, SelectMenuComponent, SelectMode} from '../../../shared/components/select-menu/select-menu.component';
import {CommonModule} from '@angular/common';
import {toSnakeCase} from '../../../utils/strings';
import {MatDialog} from '@angular/material/dialog';
import {AuthorizationService} from '../../../services/authorization.service';
import {Permissions} from '../../../shared/permissions';
import {ConfirmTypeToDeleteModalComponent} from '../../../shared/components/confirm-type-to-delete-modal/confirm-type-to-delete-modal.component';

@Component({
  selector: 'app-account-modal',
  imports: [
    ReactiveFormsModule,
    TitledInputComponent,
    ButtonComponent,
    SelectMenuComponent,
    CommonModule
  ],
  templateUrl: './account-modal.component.html',
  styleUrl: './account-modal.component.scss'
})
export class AccountModalComponent {

  public currentMode: FormMode = FormMode.ADD;
  form: FormGroup<{
    name: FormControl<string | null>;
    key: FormControl<string | null>;
    brands: FormControl<string[] | null>;
  }>;
  public configs = {
    [FormMode.ADD]: {
      title: 'Add Account',
      submitText: 'Add',
      submitFunction: () => this.addAccount()
    },
    [FormMode.EDIT]: {
      title: 'Edit Account',
      submitText: 'Update',
      submitFunction: () => this.editAccount()
    }
  };
  public accountUsers?: string[];
  public brandOptions: SelectItem[] = [];
  public canDelete = false;
  public data = inject(MAT_DIALOG_DATA);
  public redirectCallback = (result: ResultData) => {
    window.open(`https://${result.value}`, '_blank', 'noopener');
  }
  protected readonly FormMode = FormMode;
  protected readonly SelectMode = SelectMode;
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  constructor(private dialogRef: MatDialogRef<AccountModalComponent>, private managementService: ManagementService,
              private stateService: StateService, private utilService: UtilService,
              private dialog: MatDialog, private authorizationService: AuthorizationService) {
    this.canDelete = this.authorizationService.hasPermissions([Permissions.DELETE_ACCOUNT]);
    this.form = this.fb.group({
      key: new FormControl({
        value: '',
        disabled: !!this.data
      }, [CustomValidators.required('Account key'), CustomValidators.brandKey('Account key')]),
      name: new FormControl('', {validators:[CustomValidators.required('Account name')], updateOn: 'blur'}),
      brands: new FormControl<string[]>([]),
    });
    if (this.data){
      this.currentMode = FormMode.EDIT;
      this.managementService.getAccountUsers(this.data.accountKey)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (users) => {
            this.accountUsers = this.userResponseToStringArray(users);
          },
          error: () => {
            // Degrade gracefully — show no users rather than letting the error reach the global handler.
            this.accountUsers = [];
          }
        });
      this.form.patchValue({
        ...this.data,
        key: this.data.accountKey,
        brands: this.data.brands.map((brand: BrandSummary) => brand.brandKey)
      });
    }
    this.managementService.brandsList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((brands) => {
        this.brandOptions = objectArrayToSelectOptions(brands, "brandKey", "name");
      });
    this.form.get('name')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string | null) => {
      if (!this.data && value) {
        const snake = toSnakeCase(value);
        this.form.get('key')?.setValue(snake, { emitEvent: false });
      }
    });

  }

  get key(): FormControl {
    return this.form.get('key') as FormControl;
  }

  get name(): FormControl {
    return this.form.get('name') as FormControl;
  }

  get brands(): FormControl<string[]> {
    return this.form.get('brands') as FormControl<string[]>;
  }

  submit() {
    this.stateService.loading = true;
    this.configs[this.currentMode].submitFunction();
  }

  cancel() {
    this.dialogRef.close();
  }

  addAccount() {
    this.managementService.addAccount(this.getAccountPayload()).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: result => {
            this.managementService.fetchAndStoreAccounts();
            this.dialogRef.close(result)
          },
          error: error => {
            this.stateService.loading = false;
            this.utilService.openSnackBar(error?.error?.message ?? 'Something went wrong. Please try again.', {variant: 'error', position: 'center'}
            )
          }
        }
      )
  }

  editAccount() {
    this.managementService.editAccount(this.getAccountPayload()).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: result => {
            this.dialogRef.close(result)
          },
          error: error => {
            this.stateService.loading = false;
            this.utilService.openSnackBar(error?.error?.message ?? 'Something went wrong. Please try again.', {variant: 'error', position: 'center'})
          }
        }
      );
  }

  deleteAccount() {
    // The account row already carries its users (the list is loaded with elaborate=true), so gate on
    // that count synchronously — no need to wait on the modal's getAccountUsers profile fetch. Fall
    // back to the fetched accountUsers count if userIds isn't present (defensive — the server rejects
    // a non-empty account either way). Only empty accounts can be deleted; surface the reason only
    // here, on an actual delete attempt — not as a standing warning while the admin is just editing.
    if ((this.data?.userIds?.length ?? this.accountUsers?.length ?? 0) > 0) {
      this.utilService.openSnackBar('Remove all users before this account can be deleted.', {
        variant: 'error',
        position: 'center'
      });
      return;
    }
    const dialogRef = this.dialog.open(ConfirmTypeToDeleteModalComponent, {
      data: {
        header: 'Delete account',
        description: `This permanently deletes the account ${this.data.name} (${this.data.accountKey}) and its data. This action is irreversible.`,
        confirmLabel: 'Delete account',
        expectedValue: this.data.name
      },
      panelClass: 'management-modal',
      autoFocus: false
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }
      this.stateService.loading = true;
      this.managementService.deleteAccount(this.data.accountKey).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.dialogRef.close({deleted: true, accountKey: this.data.accountKey, name: this.data.name}),
          error: error => {
            this.stateService.loading = false;
            this.utilService.openSnackBar(error?.error?.message ?? 'Something went wrong. Please try again.', {variant: 'error', position: 'center'});
          }
        });
    });
  }

  private userResponseToStringArray(users: AccountUsersResponse): string[] {
    return users.accountUsers.map(user => `${user.firstName} ${user.lastName} (${user.email})`);
  }

  private getAccountPayload() {
    const form = this.form.getRawValue();
    return {
      accountKey: form.key,
      name: form.name,
      brandKeys: form.brands,
    } as AccountPayload;
  }

}
